/**
 * ProShip Direct v2 — the two field sets a courier account carries, rendered once.
 *
 * Credentials and contract attributes are asked for twice — when the account is connected, and
 * whenever it is edited — and the two must not drift. They live here so the connect page and the
 * edit drawer render the same fields from the same source.
 *
 * Credentials are per carrier, in the carrier's own words: Bluedart issues a Licence key, Ekart a
 * Client secret, Xpressbees an email and password. Flattening those into "API key / API secret"
 * is how a merchant pastes the right value into the wrong box, so the labels come straight from
 * carrierSchema.js.
 */
import React from 'react';
import { Box, Grid, Typography, Tooltip, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

import { RHFTextField, RHFSelectField } from '../../../common/hook-form';
import { SUBTLE, NAVY, BORDER, LIGHT_BG, SUCCESS } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { MONO } from './directCellStyles';
import { GAP } from '../../../styles/foundation/spacing';

import { carrierSchemas } from '../data/carrierSchema';
import { COURIER_META_GROUPS, webhookUrlFor } from '../data/defaults';

const helpSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 };
const groupTitleSx = {
  fontFamily: FONT,
  fontSize: SIZE['3xs'],
  fontWeight: WEIGHT.bold,
  color: SUBTLE,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  mb: 0.75,
};

/**
 * Fields the carrier itself does not define, and that this module asks for elsewhere.
 *
 * `accountName` duplicates the account code, which the merchant has already given; `serviceLine`
 * is the reviewed build's service-line concept, which this module does not have. Both are
 * filtered here rather than edited out of the copied schema, so the copy stays a copy.
 */
const SKIP_FIELDS = ['accountName', 'serviceLine'];

/** Carriers not in the schema still need something to authenticate with. */
const GENERIC_FIELDS = [
  { name: 'apiKey', label: 'API key', required: true, type: 'text' },
  { name: 'apiSecret', label: 'API secret', required: true, type: 'password' },
];

/**
 * Catalogue name to schema key. The ProShip catalogue writes "Blue Dart"; the schema keys on
 * BLUEDART, so punctuation and spacing are dropped rather than matched exactly.
 */
export const carrierKeyFor = (name) => String(name || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

/** What this carrier asks for, or a generic key/secret pair if we do not know it yet. */
export function credentialFieldsFor(carrierKey) {
  const schema = carrierSchemas[carrierKeyFor(carrierKey)];
  if (!schema) return GENERIC_FIELDS;
  return schema.fields.filter((f) => !SKIP_FIELDS.includes(f.name));
}

/** True when we hold the carrier's real field list, rather than falling back to key/secret. */
export const hasCarrierSchema = (carrierKey) => Boolean(carrierSchemas[carrierKeyFor(carrierKey)]);

/**
 * Credential inputs for one carrier.
 *
 * `prefix` namespaces them under a single form key ('cred'), so the values come back off the form
 * as one object ready to store against the account.
 */
export function CredentialFields({ carrierKey, prefix = 'cred' }) {
  const fields = credentialFieldsFor(carrierKey);

  return (
    <Grid container spacing={2}>
      {fields.map((field) => (
        <Grid item xs={12} md={6} key={field.name}>
          {field.type === 'select' ? (
            <RHFSelectField
              name={`${prefix}.${field.name}`}
              label={field.label}
              width='100%'
              options={(field.options || []).map((o) => ({ value: o, label: o }))}
              mandatory={field.required}
            />
          ) : (
            <RHFTextField
              name={`${prefix}.${field.name}`}
              label={field.label}
              type={field.type === 'password' ? 'password' : 'text'}
              mandatory={field.required}
            />
          )}
          {field.help && <Typography sx={{ ...helpSx, mt: 0.4 }}>{field.help}</Typography>}
        </Grid>
      ))}
    </Grid>
  );
}

/**
 * Contract attributes — the limits allocation needs before it can send anything to this account.
 *
 * Unlike serviceability or zones there is no ProShip default to inherit: these are numbers from
 * the merchant's own contract, and guessing them means either refusing shipments the carrier
 * would have taken or handing over shipments it will reject at pickup.
 */
export function CourierMetaFields({ prefix = 'meta' }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.lg }}>
      {COURIER_META_GROUPS.map((group) => (
        <Box key={group.key}>
          <Typography sx={groupTitleSx}>{group.title}</Typography>
          <Grid container spacing={2}>
            {group.fields.map((field) => (
              <Grid item xs={6} md={3} key={field.key}>
                {field.type === 'select' ? (
                  <RHFSelectField
                    name={`${prefix}.${field.key}`}
                    label={field.label}
                    width='100%'
                    options={(field.options || []).map((o) => ({ value: o, label: o }))}
                    mandatory={field.required}
                  />
                ) : (
                  <RHFTextField
                    name={`${prefix}.${field.key}`}
                    label={field.unit ? `${field.label} (${field.unit})` : field.label}
                    type={field.type}
                    placeholder={field.placeholder}
                    mandatory={field.required}
                    {...(field.defaultValue !== undefined ? { InputLabelProps: { shrink: true, sx: { fontSize: 10 } } } : {})}
                    // A time input has no placeholder to shrink the label off, so it is shrunk
                    // outright. Spread as an object because passing InputLabelProps={undefined}
                    // would overwrite RHFTextField's own 10px label styling with nothing.
                    {...(field.type === 'time' ? { InputLabelProps: { shrink: true, sx: { fontSize: 10 } } } : {})}
                  />
                )}
                {field.help && <Typography sx={{ ...helpSx, mt: 0.4 }}>{field.help}</Typography>}
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
}

/**
 * The URL the carrier posts status updates to — one per carrier.
 *
 * A merchant has to paste this into the carrier's own panel, so it is shown wherever they are
 * already looking at the account rather than left in a document. It is the same URL for every
 * contract they hold with that carrier: the update carries an AWB, and ProShip already knows
 * which account issued it.
 */
export function WebhookPanel({ carrierName, dense = false }) {
  const [copied, setCopied] = React.useState(false);
  const url = webhookUrlFor(carrierName);

  const onCopy = () => {
    try {
      navigator.clipboard?.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (err) {
      /* clipboard unavailable — the URL is selectable on screen */
    }
  };

  return (
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: 1.5,
        bgcolor: LIGHT_BG,
        p: dense ? 1 : 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          sx={{
            fontFamily: MONO,
            fontSize: SIZE['3xs'],
            color: NAVY,
            wordBreak: 'break-all',
            flex: 1,
          }}
        >
          {url}
        </Typography>
        <Tooltip title={copied ? 'Copied' : 'Copy webhook URL'}>
          <IconButton size='small' onClick={onCopy} sx={{ p: '3px' }} aria-label='copy webhook URL'>
            {copied ? (
              <CheckIcon sx={{ fontSize: 14, color: SUCCESS }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: 14, color: SUBTLE }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      {!dense && (
        <Typography sx={{ ...helpSx, mt: 0.5 }}>
          Paste this into {carrierName || 'the carrier'}&rsquo;s webhook settings — once, for all
          your {carrierName || 'carrier'} courier codes. Until you do, tracking updates only when
          ProShip polls, which is slower.
        </Typography>
      )}
    </Box>
  );
}
